import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, BookOpen, Copy, KeyRound, Pencil, RefreshCcw, Trash2, X } from 'lucide-react';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';
import { settingsApi } from '../../../api/settings.api';

const emptyForm = {
  name: '',
  whitelisted_urls: '',
};

const toMultiline = (urls) => (Array.isArray(urls) ? urls.join('\n') : '');

const parseWhitelist = (value) =>
  String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 1);

const maskToken = (value) => {
  const token = String(value || '');
  if (token.length <= 18) return token;
  return `${token.slice(0, 10)}...${token.slice(-6)}`;
};

const ApiTokenPage = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedToken, setSelectedToken] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [revealedToken, setRevealedToken] = useState('');
  const { showSuccess, showError } = useUIStore();
  const { confirm } = useConfirm();
  const tokenLimitReached = tokens.length >= 2;

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const response = await settingsApi.getTokens();
      setTokens(response?.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch API tokens:', error);
      showError('Failed to load API tokens');
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  const handleOpenCreate = () => {
    setMode('create');
    setSelectedToken(null);
    setForm(emptyForm);
    setRevealedToken('');
    setShowDialog(true);
  };

  const handleOpenEdit = (token) => {
    setMode('edit');
    setSelectedToken(token);
    setForm({
      name: token.name || '',
      whitelisted_urls: toMultiline(token.whitelisted_urls),
    });
    setRevealedToken('');
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedToken(null);
    setForm(emptyForm);
    setRevealedToken('');
  };

  const handleChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    whitelisted_urls: parseWhitelist(form.whitelisted_urls),
  });

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showError('Token name is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (mode === 'create') {
        const response = await settingsApi.createToken(payload);
        const record = response?.data?.data || {};
        setRevealedToken(record.token || '');
        showSuccess('API token generated successfully');
      } else if (selectedToken?.id) {
        await settingsApi.updateToken(selectedToken.id, payload);
        showSuccess('API token updated successfully');
        handleCloseDialog();
      }
      await fetchTokens();
    } catch (error) {
      console.error('Failed to save token:', error);
      showError(error?.response?.data?.error || 'Failed to save API token');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerate = async (token) => {
    const isConfirmed = await confirm({
      title: 'Regenerate Token',
      message: `Regenerate token "${token.name}"? Existing integrations will need the new token immediately.`,
      confirmText: 'Regenerate',
      confirmColor: 'warning',
      severity: 'warning',
    });
    if (!isConfirmed) return;

    try {
      const response = await settingsApi.regenerateToken(token.id);
      const record = response?.data?.data || {};
      await fetchTokens();
      setMode('regenerated');
      setSelectedToken(record);
      setForm({
        name: record.name || '',
        whitelisted_urls: toMultiline(record.whitelisted_urls),
      });
      setRevealedToken(record.token || '');
      setShowDialog(true);
      showSuccess('API token regenerated successfully');
    } catch (error) {
      console.error('Failed to regenerate token:', error);
      showError(error?.response?.data?.error || 'Failed to regenerate token');
    }
  };

  const handleDelete = async (token) => {
    const isConfirmed = await confirm({
      title: 'Delete Token',
      message: `Delete token "${token.name}"? Client calls using this token will stop working.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!isConfirmed) return;

    try {
      await settingsApi.deleteToken(token.id);
      showSuccess('API token deleted successfully');
      await fetchTokens();
    } catch (error) {
      console.error('Failed to delete token:', error);
      showError(error?.response?.data?.error || 'Failed to delete token');
    }
  };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      showSuccess('Copied to clipboard');
    } catch (_error) {
      showError('Failed to copy token');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1180, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} mb={3}>
        <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/settings">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            API Token Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate and manage template API tokens. Keep the token page focused on credentials and use the docs page for developer integration examples.
          </Typography>
        </Box>
        <Button variant="contained" color="inherit" href="/settings/template-api-docs" startIcon={<BookOpen size={16} />}>
          Open Docs
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Maximum 2 tokens are allowed. Each token can contain only 1 whitelisted URL. If a client needs 2 URLs, create 2 separate tokens.
      </Alert>

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
          background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Client Tokens
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One token maps to one whitelisted URL.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<KeyRound size={16} />}
              onClick={handleOpenCreate}
              disabled={tokenLimitReached}
            >
              Generate Token
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {tokenLimitReached ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Token limit reached. Delete an existing token before creating a new one.
        </Alert>
      ) : null}

      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)' }}>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Token Name</TableCell>
                  <TableCell>Whitelist URL</TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && tokens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                        No API tokens found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tokens.map((token) => (
                    <TableRow key={token.id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{token.name}</Typography>
                      </TableCell>
                      <TableCell>
                        {(token.whitelisted_urls || []).length ? (
                          <Chip label={token.whitelisted_urls[0]} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No whitelist
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography component="code" variant="body2">
                            {maskToken(token.token)}
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(token.token)}>
                            <Copy size={14} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell>{token.created_at ? new Date(token.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => handleOpenEdit(token)}>
                            <Pencil size={16} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRegenerate(token)}>
                            <RefreshCcw size={16} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(token)}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {mode === 'create' ? 'Generate API Token' : mode === 'regenerated' ? 'Regenerated API Token' : 'Edit API Token'}
          <IconButton onClick={handleCloseDialog}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {revealedToken ? (
            <Stack spacing={2}>
              <Alert severity="success">
                Copy this token now. Treat it like a password and share it only with the client who owns this whitelist URL.
              </Alert>
              <TextField fullWidth label="Token" value={revealedToken} InputProps={{ readOnly: true }} />
              <Button variant="outlined" startIcon={<Copy size={14} />} onClick={() => copyToClipboard(revealedToken)}>
                Copy Token
              </Button>
              {mode === 'create' ? (
                <Typography variant="body2" color="text.secondary">
                  You can reopen this token later to change its single whitelist URL or regenerate it.
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField fullWidth label="Token Name" value={form.name} onChange={handleChange('name')} />
              <TextField
                fullWidth
                label="Whitelisted URL"
                placeholder="https://client.example.com"
                value={form.whitelisted_urls}
                onChange={handleChange('whitelisted_urls')}
                helperText="Only 1 URL is allowed for each token. Create a second token for a second URL."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {revealedToken ? (
            <Button variant="contained" onClick={handleCloseDialog}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                {submitting ? (mode === 'create' ? 'Generating...' : 'Saving...') : mode === 'create' ? 'Generate Token' : 'Save Changes'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiTokenPage;
